import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import DashboardCard from './DashboardCard';

// Mockup parity: bucket hues follow the sample image (low balance red/amber →
// healthy blue/green → hoarding purple). Values are always readable via axis
// labels and the tooltip; color is reinforcement, not the only signal.
const BUCKET_COLORS = ['#e34948', '#eda100', '#2a78d6', '#2a78d6', '#1baf7a', '#4a3aa7'];

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 12px;
`;

const StatBox = styled.div`
  border: 1px solid #E5EAF0;
  border-radius: 6px;
  padding: 10px 12px;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: #6B7280;
  margin-top: 2px;
`;

export default function BalanceHistogramCard({ slice }) {
  const { t, i18n } = useTranslation();
  const data = slice.data;
  const buckets = data?.buckets || [];
  const hasAny = buckets.some((b) => b.value > 0);

  const fmt = (v) => Number(v ?? 0).toLocaleString(i18n.language, { maximumFractionDigits: 1 });

  return (
    <DashboardCard
      cardKey="izin-bakiye-dagilimi"
      title={t('leaveDashboard.histogram.title')}
      meta={t('leaveDashboard.histogram.meta')}
      span={4}
      spanMd={3}
      loading={slice.loading}
      empty={!data || !hasAny}
      domId="lvd-balance"
    >
      {data && (
        <div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={buckets} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={{ stroke: '#E1E0D9' }}
                tick={{ fontSize: 10, fill: '#898781' }}
              />
              <YAxis hide />
              <Tooltip
                cursor={{ fill: 'rgba(2, 145, 239, 0.05)' }}
                formatter={(value) => [t('leaveDashboard.histogram.people', { count: value }), null]}
                labelFormatter={(label) => t('leaveDashboard.histogram.bucketLabel', { range: label })}
              />
              <Bar dataKey="value" barSize={22} radius={[4, 4, 0, 0]}>
                {buckets.map((entry, index) => (
                  <Cell key={entry.label} fill={BUCKET_COLORS[index % BUCKET_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <StatsRow>
            <StatBox>
              <StatValue>{data.expiringRiskCount}</StatValue>
              <StatLabel>{t('leaveDashboard.histogram.expiringRisk')}</StatLabel>
            </StatBox>
            <StatBox>
              <StatValue>{fmt(data.avgRemainingDays)}</StatValue>
              <StatLabel>{t('leaveDashboard.histogram.avgRemaining')}</StatLabel>
            </StatBox>
          </StatsRow>
        </div>
      )}
    </DashboardCard>
  );
}
